// ─────────────────────────────────────────────────────────────
//  src/pages/NotaryJobDetail.js
//  NOTARY JOB DETAIL — Full-page expanded view of a single job
//  Route: /notary/jobs/:jobId
//  Triggered by "📋 View Full Job Details →" button in NotaryPortal
//  🛡️ QC GATE: "Mark Complete" disabled until scanbacks uploaded
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { sendJobCompleted, sendStatusUpdate } from '../lib/email'
import { smsJobCompleted, smsAppointmentConfirmed, smsNotaryArrived } from '../lib/sms'
import { trackCompletion } from '../lib/metrics'
import JobMessages from '../components/JobMessages'
import toast from 'react-hot-toast'

// ── AUTO COMPRESS FILE before upload ──────────────────────────
async function compressFile(file) {
  if (file.type.startsWith('image/')) {
    return new Promise((resolve) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img
        const MAX = 1800
        if (width > MAX || height > MAX) {
          const ratio = Math.min(MAX/width, MAX/height)
          width  = Math.round(width  * ratio)
          height = Math.round(height * ratio)
        }
        canvas.width  = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        canvas.toBlob(blob => {
          URL.revokeObjectURL(url)
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }))
        }, 'image/jpeg', 0.82)
      }
      img.src = url
    })
  }
  return file
}

// ── STATUS CONFIG ─────────────────────────────────────────────
const STATUS_CONFIG = {
  new:                  { label:'New',                   color:'#7C6A2E', bg:'#FDF6E3', dot:'#C9A84C' },
  pending_acceptance:   { label:'Awaiting Acceptance',   color:'#1A4A6B', bg:'#EBF4FF', dot:'#F59E0B' },
  assigned:             { label:'Assigned to You',       color:'#1A4A6B', bg:'#EBF4FF', dot:'#3B82F6' },
  confirmed:            { label:'Confirmed',             color:'#1A4A2E', bg:'#EAF5EE', dot:'#2D7A4F' },
  in_progress:          { label:'In Progress',           color:'#1A3A5E', bg:'#EBF4FF', dot:'#185FA5' },
  scanbacks_uploaded:   { label:'Scan-Backs Uploaded',   color:'#1A4A3E', bg:'#E8F5F0', dot:'#2D7A6A' },
  completed:            { label:'Completed',             color:'#1A4A2E', bg:'#EAF5EE', dot:'#5BA882' },
  awaiting_review:      { label:'Awaiting Title Review', color:'#3A1A5E', bg:'#F0EBF8', dot:'#6A3A9A' },
  paid:                 { label:'Paid ✓',                color:'#1A3A5E', bg:'#EBF4FF', dot:'#5BA882' },
  closed:               { label:'Closed',                color:'#2A2A2A', bg:'#F4F4F4', dot:'#8A8A8A' },
  cancelled:            { label:'Cancelled',             color:'#6B2020', bg:'#FDF2F2', dot:'#E24B4A' },
}

// ── STYLES ────────────────────────────────────────────────────
const NJD_STYLES = `
  :focus-visible { outline:2px solid #C9A84C; outline-offset:3px; border-radius:4px; }
  .lca-skip { position:absolute; top:-100px; left:16px; background:#C9A84C; color:#0D1B3E; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; text-decoration:none; z-index:9999; transition:top .2s; }
  .lca-skip:focus { top:16px; }
  .njd-card { background:#fff; border:1px solid #E8E6DF; border-radius:14px; overflow:hidden; margin-bottom:16px; }
  .njd-card-header { padding:14px 20px; background:linear-gradient(135deg,#0D1B3E,#1A3A5E); color:#C9A84C; font-family:'Cormorant Garamond',serif; font-size:18px; font-weight:600; letter-spacing:0.04em; }
  .njd-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
  @media(max-width:760px){ .njd-grid-2 { grid-template-columns:1fr; } }
  .njd-info-cell { padding:12px 16px; border-bottom:1px solid #F0EEE8; }
  .njd-info-cell:last-child { border-bottom:none; }
  .njd-label { font-size:10px; color:#B8B5AB; letter-spacing:0.08em; font-weight:600; margin-bottom:4px; text-transform:uppercase; }
  .njd-value { font-size:14px; color:#0D1B3E; font-weight:500; }
  .back-btn { display:inline-flex; align-items:center; gap:6px; background:none; border:none; color:#9A7A30; font-size:13px; font-weight:600; cursor:pointer; padding:8px 0; margin-bottom:8px; }
  .back-btn:hover { color:#0D1B3E; }
  .qc-gate-banner { background:#FDF6E3; border:2px solid #C9A84C; border-radius:12px; padding:14px 18px; margin-bottom:16px; }
  .qc-pass-banner { background:#EAF5EE; border:2px solid #2D7A4F; border-radius:12px; padding:14px 18px; margin-bottom:16px; }
  .complete-banner { background:linear-gradient(135deg,#2D7A4F,#1A4A2E); border-radius:14px; padding:18px 22px; margin-bottom:16px; color:#fff; }
  .maps-btn { display:inline-flex; align-items:center; gap:6px; padding:10px 18px; border-radius:10px; background:#EAF5EE; color:#2D7A4F; font-size:13px; font-weight:600; border:1px solid rgba(45,122,79,0.3); text-decoration:none; transition:all 0.15s; }
  .maps-btn:hover { background:#2D7A4F; color:#fff; }
  .upload-zone { display:block; padding:24px 20px; background:#FDF6E3; border:2px dashed #C9A84C; border-radius:12px; text-align:center; cursor:pointer; margin-top:10px; }
  .upload-zone:hover { background:#FFFBE8; }
`

// ── BACK BUTTON ───────────────────────────────────────────────
function BackToSignings() {
  const navigate = useNavigate()
  return (
    <button type="button" className="back-btn" onClick={() => navigate('/notary/signings')}>
      ← Back to My Signings
    </button>
  )
}

// ── STATUS BADGE ──────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new
  return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:10, background:cfg.bg, border:`2px solid ${cfg.dot}40`, borderRadius:24, padding:'8px 18px' }}>
      <div style={{ width:10, height:10, borderRadius:'50%', background:cfg.dot, boxShadow:`0 0 8px ${cfg.dot}` }} />
      <span style={{ fontSize:14, fontWeight:700, color:cfg.color, letterSpacing:'0.04em' }}>{cfg.label}</span>
    </div>
  )
}

// ── MAPS BUTTON ───────────────────────────────────────────────
function MapsBtn({ address, city, state, zip }) {
  const fullAddress = encodeURIComponent(`${address}, ${city}, ${state} ${zip || ''}`.trim())
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  const url = isIOS
    ? `https://maps.apple.com/?daddr=${fullAddress}`
    : `https://www.google.com/maps/dir/?api=1&destination=${fullAddress}`
  return (
    <a href={url} target="_blank" rel="noreferrer" className="maps-btn">
      🗺️ Open in Maps
    </a>
  )
}

// ── DOCUMENT VAULT (notary side) ──────────────────────────────
function NotaryDocSection({ jobId, onScanBacksChange }) {
  const [docs,      setDocs]      = useState([])
  const [uploading, setUploading] = useState(false)

  useEffect(() => { loadDocs() }, [jobId])

  async function loadDocs() {
    const { data } = await supabase.from('job_documents').select('*').eq('job_id', jobId)
      .order('created_at', { ascending: false })
    setDocs(data || [])
    // Notify parent of scanback count
    if (onScanBacksChange) {
      const sbs = (data || []).filter(d => d.doc_type === 'scan_back' || d.doc_type === 'completed_docs')
      onScanBacksChange(sbs.length)
    }
  }

  async function recordDownload(doc) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      await supabase.from('job_documents').update({
        downloaded_at: new Date().toISOString(),
        downloaded_by: user?.id,
      }).eq('id', doc.id)
      await supabase.from('jobs').update({ docs_downloaded_at: new Date().toISOString() }).eq('id', jobId)
      window.open(doc.file_url, '_blank')
      loadDocs()
    } catch (err) {
      console.error('Download tracking error:', err)
      window.open(doc.file_url, '_blank')
    }
  }

  async function uploadCompleted(e) {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true)
    try {
      const compressed = await compressFile(file)
      const ext  = compressed.name.split('.').pop()
      const path = `${jobId}/scanback-${Date.now()}.${ext}`
      const { error } = await supabase.storage.from('job-documents').upload(path, compressed, { upsert: true })
      if (error) throw error
      const { data: { publicUrl } } = supabase.storage.from('job-documents').getPublicUrl(path)
      const { data: { user } } = await supabase.auth.getUser()
      await supabase.from('job_documents').insert({
        job_id: jobId, uploaded_by: user?.id, role: 'notary',
        doc_type: 'scan_back', file_name: file.name,
        file_url: publicUrl, file_size: file.size, qc_status: 'pending',
      })
      toast.success('✅ Scan-back uploaded!! Title company will be notified for QC review!!')
      e.target.value = ''
      loadDocs()
    } catch (err) {
      toast.error('Upload failed: ' + err.message)
    } finally {
      setUploading(false)
    }
  }

  const signingPkg = docs.filter(d => d.doc_type === 'signing_package')
  const scanBacks  = docs.filter(d => d.doc_type === 'scan_back' || d.doc_type === 'completed_docs')

  return (
    <div className="njd-card">
      <div className="njd-card-header">📁 Document Vault</div>
      <div style={{ padding:'18px 20px' }}>

        {/* SIGNING PACKAGE — DOWNLOAD */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#185FA5', marginBottom:8, letterSpacing:'0.06em' }}>📦 SIGNING PACKAGE FROM CLIENT</div>
          {signingPkg.length === 0 ? (
            <div style={{ background:'#F8F7F4', borderRadius:8, padding:'14px 16px', fontSize:12, color:'#B8B5AB' }}>
              📦 No signing package uploaded yet by client!! Contact client if needed!!
            </div>
          ) : (
            signingPkg.map(d => (
              <div key={d.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'#EBF4FF', borderRadius:8, marginBottom:6 }}>
                <div>
                  <div style={{ fontSize:13, color:'#0D1B3E', fontWeight:500 }}>📄 {d.file_name}</div>
                  {d.downloaded_at && <div style={{ fontSize:10, color:'#2D7A4F', marginTop:2 }}>✅ Downloaded {new Date(d.downloaded_at).toLocaleString()}</div>}
                </div>
                <button onClick={() => recordDownload(d)}
                  style={{ background:'#185FA5', color:'#fff', fontSize:12, fontWeight:700, padding:'8px 16px', borderRadius:8, border:'none', cursor:'pointer' }}>
                  ⬇️ Download
                </button>
              </div>
            ))
          )}
        </div>

        {/* SCAN-BACKS — UPLOAD */}
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:'#2D7A4F', marginBottom:8, letterSpacing:'0.06em' }}>📤 SCAN-BACKS YOU UPLOADED</div>
          {scanBacks.length === 0 ? (
            <div style={{ fontSize:12, color:'#B8B5AB', marginBottom:10, padding:'8px 0' }}>
              No scan-backs uploaded yet!! ⚠️ <strong>Required before marking job complete!!</strong>
            </div>
          ) : (
            scanBacks.map(d => (
              <div key={d.id} style={{ marginBottom:10, padding:'10px 14px', background:'#FAFAF8', borderRadius:8, border:'1px solid #F0EEE8' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
                  <div style={{ fontSize:13, color:'#0D1B3E', fontWeight:500 }}>📄 {d.file_name}</div>
                  <span style={{ fontSize:10, fontWeight:600, padding:'3px 10px', borderRadius:10,
                    background: d.qc_status === 'approved' ? '#EAF5EE' : d.qc_status === 'rejected' ? '#FDF2F2' : '#FDF6E3',
                    color: d.qc_status === 'approved' ? '#2D7A4F' : d.qc_status === 'rejected' ? '#C0392B' : '#8A5C00'
                  }}>
                    {d.qc_status === 'approved' ? '✓ Approved — Ship Docs!!' : d.qc_status === 'rejected' ? '❌ Corrections Needed' : '⏳ Pending QC'}
                  </span>
                </div>
                <div style={{ fontSize:10, color:'#B8B5AB' }}>{new Date(d.created_at).toLocaleString()}</div>
                {d.qc_status === 'rejected' && d.qc_correction_notes && (
                  <div style={{ background:'#FDF2F2', border:'1px solid rgba(226,75,74,0.2)', borderRadius:6, padding:'8px 10px', marginTop:8 }}>
                    <div style={{ fontSize:11, fontWeight:600, color:'#C0392B', marginBottom:3 }}>📝 Corrections needed from Title:</div>
                    <div style={{ fontSize:12, color:'#E24B4A' }}>{d.qc_correction_notes}</div>
                    <div style={{ fontSize:10, color:'#B8B5AB', marginTop:4 }}>Upload a corrected scan-back below!!</div>
                  </div>
                )}
                {d.qc_status === 'approved' && (
                  <div style={{ fontSize:11, color:'#2D7A4F', marginTop:4, fontWeight:600 }}>
                    ✅ Title approved — you can now ship the documents!!
                  </div>
                )}
              </div>
            ))
          )}

          {/* UPLOAD ZONE */}
          <label htmlFor={`scanback-${jobId}`} style={{ cursor: uploading ? 'wait' : 'pointer' }}>
            <input id={`scanback-${jobId}`} type="file" accept=".pdf,.jpg,.jpeg,.png" capture="environment" style={{ display:'none' }} onChange={uploadCompleted} disabled={uploading} />
            <div className="upload-zone">
              {uploading ? (
                <div>
                  <div style={{ fontSize:32, marginBottom:8 }}>⏳</div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#8A5C00' }}>Uploading scan-back...</div>
                  <div style={{ fontSize:11, color:'#B8A060', marginTop:4 }}>Auto-compressing for fast upload!!</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize:36, marginBottom:8 }}>📤</div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#0D1B3E', marginBottom:4 }}>Upload Scan-Back</div>
                  <div style={{ fontSize:12, color:'#7A7870', marginBottom:8 }}>Tap to browse or drag PDF/JPG/PNG here</div>
                  <div style={{ display:'inline-block', background:'#C9A84C', color:'#0D1B3E', fontSize:13, fontWeight:700, padding:'8px 20px', borderRadius:8 }}>+ Choose File</div>
                </div>
              )}
            </div>
          </label>
        </div>
      </div>
    </div>
  )
}

// ── TRACKING INPUT ────────────────────────────────────────────
function TrackingInput({ jobId, currentTracking }) {
  const [tracking, setTracking] = useState(currentTracking || '')
  const [saved,    setSaved]    = useState(!!currentTracking)
  const [saving,   setSaving]   = useState(false)

  async function saveTracking() {
    if (!tracking.trim()) return toast.error('Please enter a tracking number')
    setSaving(true)
    try {
      await supabase.from('jobs').update({ tracking_number: tracking.trim() }).eq('id', jobId)
      setSaved(true)
      toast.success('📦 Tracking number saved — client has been notified!!')
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  return (
    <div className="njd-card">
      <div className="njd-card-header">📦 Shipping Tracking</div>
      <div style={{ padding:'18px 20px' }}>
        <div style={{ fontSize:12, color:'#7A7870', marginBottom:10 }}>
          Enter the FedEx, UPS, or USPS tracking number once you've shipped the documents!!
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <input
            placeholder="e.g. 1Z999AA10123456784"
            value={tracking}
            onChange={e => { setTracking(e.target.value); setSaved(false) }}
            style={{ flex:1, minWidth:200, padding:'10px 14px', border:'1.5px solid #E0D9CC', borderRadius:10, fontSize:13, fontFamily:'inherit' }}
          />
          <button onClick={saveTracking} disabled={saving || (saved && tracking === currentTracking)}
            style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', color:'#C9A84C', border:'none', borderRadius:10, padding:'10px 18px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving...' : saved ? '✓ Saved' : '💾 Save & Notify'}
          </button>
        </div>
        {saved && tracking && (
          <div style={{ fontSize:11, color:'#2D7A4F', marginTop:8, fontWeight:600 }}>
            ✅ Saved: {tracking} — Client can track package!!
          </div>
        )}
      </div>
    </div>
  )
}

// ── MAIN PAGE ─────────────────────────────────────────────────
export default function NotaryJobDetail() {
  const { jobId } = useParams()
  const navigate  = useNavigate()
  const { user, profile }  = useAuth()
  const [job,           setJob]           = useState(null)
  const [loading,       setLoading]       = useState(true)
  const [scanBackCount, setScanBackCount] = useState(0)
  const [completing,    setCompleting]    = useState(false)

  useEffect(() => {
    if (!user || !jobId) return
    loadJob()

    // Real-time subscription
    const ch = supabase.channel(`notary-job-detail-${jobId}`)
      .on('postgres_changes', {
        event:  'UPDATE',
        schema: 'public',
        table:  'jobs',
        filter: `id=eq.${jobId}`
      }, payload => {
        setJob(prev => ({ ...prev, ...payload.new }))
      })
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [user, jobId])

  async function loadJob() {
    setLoading(true)
    const { data, error } = await supabase.from('jobs').select('*').eq('id', jobId).single()
    if (error || !data) {
      toast.error('Job not found!!')
      navigate('/notary/signings')
      return
    }
    // Security check — confirm this notary owns the job!!
    if (data.notary_id !== user?.id) {
      toast.error('Access denied — this job is not assigned to you!!')
      navigate('/notary/signings')
      return
    }
    setJob(data)
    setLoading(false)
  }

  // 🛡️ THE QC GATE — Mark complete with scanback validation!!
  async function markComplete() {
    // 🛡️ GATE: must have at least 1 scanback (skip for Individual jobs OR when client opts out)
    const skipGate = job.job_type === 'individual' || job.scan_backs_required === false
    if (!skipGate && scanBackCount === 0) {
      toast.error('🛡️ You must upload at least 1 scan-back before marking this job complete!!', { duration: 6000 })
      return
    }

    if (!window.confirm(`Mark job ${job.job_number} as complete??\n\nThis will:\n✅ Notify the client by email + SMS\n✅ Trigger payment workflow\n✅ Move job to "Awaiting Title Review"\n\nProceed??`)) {
      return
    }

    setCompleting(true)
    try {
      await supabase.from('jobs').update({ status: 'completed' }).eq('id', job.id)

      // Notify client by email
      if (job.borrower_email) {
        await sendJobCompleted({
          client_name:  job.borrower_name,
          client_email: job.borrower_email,
          job_number:   job.job_number,
          service_name: job.signing_type,
          notary_name:  profile?.full_name || null,
          amount:       job.total_fee,
        })
      }

      // Notify client by SMS
      if (job.borrower_phone) {
        await smsJobCompleted({
          client_phone: job.borrower_phone,
          job_number:   job.job_number,
          service_name: job.signing_type,
        })
      }

      // 📊 Track completion metrics
      try { await trackCompletion(user.id) } catch(e) { console.error('Metrics error:', e) }

      toast.success(`🎉 Job ${job.job_number} marked complete!! Client notified by email + SMS!!`, { duration: 5000 })
      setJob(prev => ({ ...prev, status: 'completed' }))
    } catch (err) {
      toast.error(err.message)
    } finally {
      setCompleting(false)
    }
  }

  if (loading) return (
    <main style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:300 }}>
      <div className="spinner" />
    </main>
  )

  if (!job) return null

  const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.new
  const isInProgress = ['accepted','on_the_way','arrived'].includes(job.status)
  const isCompleted  = ['completed','scanbacks_uploaded','client_approved','payment_received','notary_paid','closed'].includes(job.status)
  // ✅ Skip QC gate for: (a) individual customer jobs OR (b) clients who opted out of scanbacks
  const skipScanbackGate = job.job_type === 'individual' || job.scan_backs_required === false
  const canMarkComplete = isInProgress && (skipScanbackGate || scanBackCount > 0)
  // 🎯 Milestone states — canonical: accepted → (confirm appt = stamp confirmed_at) → arrived → completed
  const canConfirmAppt = job.status === 'accepted' && !job.confirmed_at
  const canMarkArrived = ['accepted','on_the_way'].includes(job.status) && !!job.confirmed_at
  const apptConfirmed  = !!job.confirmed_at || ['arrived','completed','scanbacks_uploaded','client_approved','payment_received','notary_paid','closed'].includes(job.status)
  const apptArrived    = ['arrived','completed','scanbacks_uploaded','client_approved','payment_received','notary_paid','closed'].includes(job.status)

  // ✅ Confirm appointment with borrower
  async function confirmAppointment() {
    if (!window.confirm(`Confirm that you've contacted the borrower and verified:\n\n• Date & Time\n• Address\n• Signers\n• ID requirements\n\nProceed??`)) return
    try {
      const now = new Date().toISOString()
      await supabase.from('jobs').update({ confirmed_at: now }).eq('id', job.id)
      // Email to borrower
      if (job.borrower_email) {
        try {
          await sendStatusUpdate({
            client_name: job.borrower_name,
            client_email: job.borrower_email,
            job_number: job.job_number,
            service_name: job.signing_type,
            status: 'Appointment Confirmed',
            message: `Your notary ${profile?.full_name || ''} has confirmed your appointment. Looking forward to meeting you!`
          })
        } catch(e) { console.error('Confirm email error:', e) }
      }
      // 📱 SMS to borrower
      if (job.borrower_phone) {
        try {
          await smsAppointmentConfirmed({
            client_phone: job.borrower_phone,
            notary_name: profile?.full_name,
            job_number: job.job_number,
            appointment: job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : null,
          })
        } catch(e) { console.error('Confirm SMS error:', e) }
      }
      // Status event for audit trail
      try {
        await supabase.from('job_status_events').insert({
          job_id: job.id,
          status: 'confirmed',
          from_status: 'accepted',
          note: '✅ Borrower appointment confirmed by notary (date/time/address/signers/IDs verified)',
          created_by_role: 'notary'
        })
      } catch(e) { console.error('Status event error:', e) }
      toast.success('✅ Appointment confirmed!! Admin + client notified by email + SMS.', { duration: 5000 })
      setJob(prev => ({ ...prev, confirmed_at: now }))
    } catch(err) {
      toast.error(err.message)
    }
  }

  // 📍 Mark arrived at appointment location
  async function markArrived() {
    if (!window.confirm(`Confirm you have ARRIVED at the appointment location??\n\nThis will notify the admin and client in real-time.`)) return
    try {
      const now = new Date().toISOString()
      await supabase.from('jobs').update({ status: 'arrived', arrived_at: now }).eq('id', job.id)
      // 📧 Email to borrower
      if (job.borrower_email) {
        try {
          await sendStatusUpdate({
            client_name: job.borrower_name,
            client_email: job.borrower_email,
            job_number: job.job_number,
            service_name: job.signing_type,
            status: 'Notary Arrived',
            message: `Your notary ${profile?.full_name || ''} has arrived at the appointment location. Your signing is now in progress!!`
          })
        } catch(e) { console.error('Arrived email error:', e) }
      }
      // 📱 SMS to borrower
      if (job.borrower_phone) {
        try {
          await smsNotaryArrived({
            client_phone: job.borrower_phone,
            notary_name: profile?.full_name,
            job_number: job.job_number,
          })
        } catch(e) { console.error('Arrived SMS error:', e) }
      }
      // Status event
      try {
        await supabase.from('job_status_events').insert({
          job_id: job.id,
          status: 'arrived',
          from_status: job.status,
          note: '📍 Notary arrived at appointment location — signing in progress',
          created_by_role: 'notary'
        })
      } catch(e) { console.error('Status event error:', e) }
      toast.success('📍 Arrival confirmed!! Client notified by email + SMS. Signing in progress.', { duration: 4000 })
      setJob(prev => ({ ...prev, status: 'arrived', arrived_at: now }))
    } catch(err) {
      toast.error(err.message)
    }
  }

  // 🔒 ACCEPTANCE GATE — until the notary accepts, NO full details, borrower contact, or documents.
  // They see only the scope needed to decide, and a path to the Accept / Decline screen.
  if (job.status === 'assigned') {
    return (
      <>
        <style>{NJD_STYLES}</style>
        <a href="#njd-main" className="lca-skip">Skip to main content</a>
        <main id="njd-main" tabIndex={-1}>
          <BackToSignings />
          <div style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', borderRadius:16, padding:'24px 28px', marginBottom:16, color:'#fff' }}>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', letterSpacing:'0.12em', marginBottom:4 }}>JOB NUMBER</div>
            <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:28, color:'#C9A84C', fontWeight:700, lineHeight:1, marginBottom:8 }}>{job.job_number}</div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)' }}>{job.signing_type}</div>
            <div style={{ marginTop:10 }}><StatusBadge status={job.status} /></div>
          </div>

          {/* Scope only — enough to decide, nothing private */}
          <div className="njd-card">
            <div className="njd-card-header">📋 Assignment Scope</div>
            <div className="njd-info-cell"><div className="njd-label">AREA</div><div className="njd-value">{job.signing_city}, {job.signing_state}</div></div>
            <div className="njd-info-cell"><div className="njd-label">APPOINTMENT</div><div className="njd-value">{job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : 'TBD — schedule with borrower after accepting'}</div></div>
            <div className="njd-info-cell"><div className="njd-label">YOUR PAY (UPON COMPLETION)</div><div className="njd-value" style={{ color:'#2D7A4F', fontWeight:700, fontSize:16 }}>${job.notary_pay}</div></div>
          </div>

          {/* The lock */}
          <div className="njd-card" style={{ border:'1px solid #F59E0B', background:'#FEF3E2' }}>
            <div style={{ padding:'18px 20px', textAlign:'center' }}>
              <div style={{ fontSize:30, marginBottom:6 }}>🔒</div>
              <div style={{ fontSize:15, fontWeight:700, color:'#9A6800', marginBottom:6 }}>Accept to unlock full details &amp; documents</div>
              <div style={{ fontSize:12.5, color:'#7A5500', lineHeight:1.6, marginBottom:14 }}>
                The borrower's contact info, full address, instructions, and signing documents become available the moment you accept. This protects the borrower's private information until you're officially on the job.
              </div>
              <a href="/notary/available" className="btn btn-primary" style={{ display:'inline-flex', minHeight:48, alignItems:'center', justifyContent:'center', padding:'0 24px', fontSize:14, fontWeight:700, borderRadius:10, textDecoration:'none' }}>
                ✅ Go to Accept / Decline →
              </a>
            </div>
          </div>

          {/* Messaging stays open — they may have questions before accepting */}
          <div className="njd-card">
            <div className="njd-card-header">💬 Messages with LCA Team</div>
            <div style={{ padding:'8px' }}>
              <JobMessages jobId={job.id} jobNumber={job.job_number} role="notary" />
            </div>
          </div>
        </main>
      </>
    )
  }

  return (
    <>
      <style>{NJD_STYLES}</style>
      <a href="#njd-main" className="lca-skip">Skip to main content</a>
      <main id="njd-main" tabIndex={-1}>

        {/* ── BACK BUTTON ── */}
        <BackToSignings />

        {/* ── HERO ── */}
        <div style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', borderRadius:16, padding:'24px 28px', marginBottom:16, color:'#fff' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:16, marginBottom:12 }}>
            <div style={{ flex:1, minWidth:200 }}>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', letterSpacing:'0.12em', marginBottom:4 }}>JOB NUMBER</div>
              <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:28, color:'#C9A84C', fontWeight:700, lineHeight:1, marginBottom:8 }}>{job.job_number}</div>
              <div style={{ fontSize:15, color:'#fff', fontWeight:500, marginBottom:4 }}>{job.borrower_name || 'Borrower TBD'}</div>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)' }}>{job.signing_type}</div>
              {job.client_po && <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:4 }}>📁 File # {job.client_po}</div>}
            </div>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:10, flexShrink:0 }}>
              <StatusBadge status={job.status} />
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', letterSpacing:'0.08em' }}>YOUR PAY</div>
                <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:24, color:'#5BA882', fontWeight:700 }}>${job.notary_pay}</div>
              </div>
            </div>
          </div>
          {job.job_type === 'individual' && (
            <span style={{ background:'rgba(106,58,154,0.3)', color:'#C9A8DD', fontSize:11, fontWeight:700, padding:'4px 12px', borderRadius:20 }}>👤 Individual Customer</span>
          )}
        </div>

        {/* ── COMPLETED BANNER ── */}
        {isCompleted && (
          <div className="complete-banner">
            <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>🎉 Job Complete!!</div>
            <div style={{ fontSize:13, opacity:0.9 }}>Great work!! The client has been notified. Payment processing in progress!!</div>
          </div>
        )}

        {/* ── 🛡️ QC GATE BANNER (when in progress) ── */}
        {isInProgress && (
          scanBackCount > 0 ? (
            <div className="qc-pass-banner">
              <div style={{ fontSize:13, fontWeight:700, color:'#1A4A2E', marginBottom:4 }}>
                ✅ Quality Gate Passed — Scan-backs uploaded!!
              </div>
              <div style={{ fontSize:12, color:'#2D7A4F' }}>
                You can now mark this job as complete!! Click the green button at the bottom!!
              </div>
            </div>
          ) : (
            <div className="qc-gate-banner">
              <div style={{ fontSize:13, fontWeight:700, color:'#8A5C00', marginBottom:4 }}>
                🛡️ Quality Gate — Scan-backs Required
              </div>
              <div style={{ fontSize:12, color:'#B8A060' }}>
                Upload at least 1 scan-back below before you can mark this job complete!! This protects you, the client, and LCA!!
              </div>
            </div>
          )
        )}

        {/* ── JOB DETAILS + CLIENT INFO ── */}
        <div className="njd-grid-2">
          <div className="njd-card">
            <div className="njd-card-header">📋 Job Details</div>
            <div className="njd-info-cell">
              <div className="njd-label">SERVICE</div>
              <div className="njd-value">{job.signing_type}</div>
            </div>
            <div className="njd-info-cell">
              <div className="njd-label">SIGNING ADDRESS</div>
              <div className="njd-value">{job.signing_address || '—'}</div>
              <div style={{ fontSize:12, color:'#7A7870', marginTop:2, marginBottom:8 }}>{job.signing_city}, {job.signing_state} {job.signing_zip}</div>
              <MapsBtn address={job.signing_address} city={job.signing_city} state={job.signing_state} zip={job.signing_zip} />
            </div>
            <div className="njd-info-cell">
              <div className="njd-label">APPOINTMENT</div>
              <div className="njd-value">{job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : 'TBD — Contact client to schedule'}</div>
            </div>
            <div className="njd-info-cell">
              <div className="njd-label">YOUR PAY (UPON COMPLETION)</div>
              <div className="njd-value" style={{ color:'#2D7A4F', fontWeight:700, fontSize:16 }}>${job.notary_pay}</div>
            </div>
          </div>

          <div className="njd-card">
            <div className="njd-card-header">👤 Client / Borrower</div>
            <div className="njd-info-cell">
              <div className="njd-label">BORROWER NAME</div>
              <div className="njd-value">{job.borrower_name || '—'}</div>
            </div>
            {job.borrower_phone && (
              <div className="njd-info-cell">
                <div className="njd-label">PHONE</div>
                <a href={`tel:${job.borrower_phone}`} className="njd-value" style={{ color:'#9A7A30', textDecoration:'none', fontWeight:600 }}>
                  📞 {job.borrower_phone}
                </a>
              </div>
            )}
            {job.borrower_email && (
              <div className="njd-info-cell">
                <div className="njd-label">EMAIL</div>
                <a href={`mailto:${job.borrower_email}`} className="njd-value" style={{ color:'#9A7A30', textDecoration:'none', fontSize:13 }}>
                  📧 {job.borrower_email}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* ── SPECIAL INSTRUCTIONS / CLIENT NOTES ── */}
        {(job.special_instructions || job.order_notes) && (
          <div className="njd-card">
            <div className="njd-card-header">💬 Important Instructions</div>
            <div style={{ padding:'14px 20px' }}>
              {job.special_instructions && (
                <div style={{ background:'#FDF6E3', border:'1px solid rgba(201,168,76,0.3)', borderRadius:10, padding:'12px 14px', marginBottom: job.order_notes ? 10 : 0 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#8A5C00', marginBottom:4 }}>📋 SPECIAL INSTRUCTIONS</div>
                  <div style={{ fontSize:13, color:'#0D1B3E', lineHeight:1.6 }}>{job.special_instructions}</div>
                </div>
              )}
              {job.order_notes && (
                <div style={{ background:'#EBF4FF', border:'1px solid rgba(24,95,165,0.2)', borderRadius:10, padding:'12px 14px' }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#1A4A6B', marginBottom:4 }}>💬 CLIENT NOTES</div>
                  <div style={{ fontSize:13, color:'#0D1B3E', lineHeight:1.6 }}>{job.order_notes}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── OFFICE PREFERENCES ── */}
        {job.office_prefs && Object.keys(job.office_prefs).some(k => k !== 'custom_note' && job.office_prefs[k] === true) && (
          <div className="njd-card">
            <div className="njd-card-header">🏛️ Office Requirements</div>
            <div style={{ padding:'14px 20px' }}>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {[
                  job.office_prefs.blue_ink    && { icon:'🖊️', label:'Blue Ink Only' },
                  job.office_prefs.no_staples  && { icon:'📎', label:'No Staples — Binder Clips Only' },
                  job.office_prefs.gps_checkin && { icon:'📍', label:'GPS Check-In Required' },
                  job.office_prefs.borrower_id && { icon:'🪪', label:'Upload Borrower ID Photo' },
                  job.office_prefs.call_before && { icon:'📞', label:'Call 30 Min Before Arrival' },
                  job.office_prefs.scan_backs  && { icon:'📄', label:'Scan-Backs Required' },
                ].filter(Boolean).map((p, i) => (
                  <span key={i} style={{ background:'#EBF4FF', color:'#1A4A6B', fontSize:12, padding:'6px 12px', borderRadius:20, border:'1px solid rgba(24,95,165,0.2)' }}>
                    {p.icon} {p.label}
                  </span>
                ))}
              </div>
              {job.office_prefs.custom_note && (
                <div style={{ marginTop:10, padding:'10px 14px', background:'#F8F7F4', borderRadius:8, fontSize:12, color:'#0D1B3E', fontStyle:'italic' }}>
                  📝 {job.office_prefs.custom_note}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── DOCUMENT VAULT (notary uploads scanbacks here!!) ── */}
        {job.job_type !== 'individual' && (
          <NotaryDocSection jobId={job.id} onScanBacksChange={setScanBackCount} />
        )}

        {/* ── SHIPPING TRACKING (after completion) ── */}
        {job.job_type !== 'individual' && ['completed','scanbacks_uploaded','client_approved'].includes(job.status) && (
          <TrackingInput jobId={job.id} currentTracking={job.tracking_number} />
        )}

        {/* ── EMBEDDED MESSAGES ── */}
        <div className="njd-card">
          <div className="njd-card-header">💬 Messages with LCA Team</div>
          <div style={{ padding:'8px' }}>
            <JobMessages jobId={job.id} jobNumber={job.job_number} role="notary" />
          </div>
        </div>

        {/* ── 🎯 MILESTONE CHECK-INS (Confirm + Arrived) ── */}
        {isInProgress && (
          <div className="njd-card">
            <div className="njd-card-header">🎯 Signing Milestones</div>
            <div style={{ padding:'18px 20px', display:'flex', flexDirection:'column', gap:12 }}>

              {/* Milestone 1: Confirm with Borrower */}
              <button
                onClick={confirmAppointment}
                disabled={!canConfirmAppt}
                style={{
                  width:'100%',
                  background: apptConfirmed ? '#E8F5EC' : canConfirmAppt ? 'linear-gradient(135deg,#0D1B3E,#1E3A6F)' : '#E8E6DF',
                  color: apptConfirmed ? '#2D7A4F' : canConfirmAppt ? '#fff' : '#B8B5AB',
                  border: apptConfirmed ? '2px solid #2D7A4F' : 'none',
                  borderRadius: 10,
                  padding: '14px 20px',
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: canConfirmAppt ? 'pointer' : 'default',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  letterSpacing: '0.02em',
                }}
              >
                {apptConfirmed ? '✅ Borrower Appointment Confirmed' : '☎️ Confirm Appointment with Borrower'}
              </button>
              {!apptConfirmed && canConfirmAppt && (
                <div style={{ fontSize:12, color:'#6B6B6B', marginTop:-4, paddingLeft:4 }}>
                  Contact borrower → verify date/time/address/signers/IDs → click button
                </div>
              )}

              {/* Milestone 2: Arrived at Appointment */}
              <button
                onClick={markArrived}
                disabled={!canMarkArrived}
                title={!canMarkArrived && !apptConfirmed ? 'Confirm appointment first' : ''}
                style={{
                  width:'100%',
                  background: apptArrived ? '#E8F5EC' : canMarkArrived ? 'linear-gradient(135deg,#C9A84C,#A88A35)' : '#E8E6DF',
                  color: apptArrived ? '#2D7A4F' : canMarkArrived ? '#fff' : '#B8B5AB',
                  border: apptArrived ? '2px solid #2D7A4F' : 'none',
                  borderRadius: 10,
                  padding: '14px 20px',
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: canMarkArrived ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  letterSpacing: '0.02em',
                }}
              >
                {apptArrived ? '✅ Arrived at Appointment' : '📍 I Have Arrived at the Appointment'}
              </button>
              {!apptArrived && canMarkArrived && (
                <div style={{ fontSize:12, color:'#6B6B6B', marginTop:-4, paddingLeft:4 }}>
                  Tap when you arrive at the signing location
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── 🛡️ MARK COMPLETE BUTTON (with QC gate!!) ── */}
        {isInProgress && (
          <div className="njd-card">
            <div className="njd-card-header">✅ Complete This Signing</div>
            <div style={{ padding:'18px 20px' }}>
              <button
                onClick={markComplete}
                disabled={!canMarkComplete || completing}
                title={!canMarkComplete ? '🛡️ Upload at least 1 scan-back first!!' : ''}
                style={{
                  width: '100%',
                  background: canMarkComplete
                    ? 'linear-gradient(135deg,#2D7A4F,#1A4A2E)'
                    : '#E8E6DF',
                  color: canMarkComplete ? '#fff' : '#B8B5AB',
                  border: 'none',
                  borderRadius: 12,
                  padding: '16px 24px',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: canMarkComplete ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  letterSpacing: '0.04em',
                  transition: 'all 0.15s',
                  opacity: completing ? 0.6 : 1,
                }}
              >
                {completing
                  ? '⏳ Processing...'
                  : canMarkComplete
                  ? '✅ Mark Job as Complete →'
                  : '🛡️ Upload Scan-Back First (Required)'
                }
              </button>
              {!canMarkComplete && (
                <div style={{ fontSize:11, color:'#8A5C00', marginTop:10, textAlign:'center', fontStyle:'italic' }}>
                  🛡️ Quality Gate: Scan-backs are required to protect you, the client, and LCA!!
                </div>
              )}
              {canMarkComplete && (
                <div style={{ fontSize:11, color:'#2D7A4F', marginTop:10, textAlign:'center' }}>
                  Marking complete will notify the client by email + SMS and trigger the payment workflow!!
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── BOTTOM BACK BUTTON ── */}
        <div style={{ marginTop:24, textAlign:'center' }}>
          <BackToSignings />
        </div>

      </main>
    </>
  )
}
